import { useCallback, useEffect, useState } from 'react'

import { getOrganizationDirectoryUsers } from '@/organizations/fetch/get-organization-directory-users'
import type { OrganizationDirectoryUserRead } from '@/organizations/schemas'
import type { OrgUserOption } from '@/matter/components/manage-members/types'
import { kUnavailableMemberEmail } from '@/matter/components/manage-members/matter-member-copy'

function mapDirectoryUserToOrgUser(user: OrganizationDirectoryUserRead): OrgUserOption {
  const email = user.email ?? kUnavailableMemberEmail
  return { userId: user.user_id, displayName: user.display_name ?? email, email }
}

/**
 * Fetches the organization directory users when enabled.
 * Used by both the Manage Members modal and the Create Matter dialog.
 */
export function useOrgDirectoryUsers(enabled: boolean): { orgUsers: OrgUserOption[]; isLoading: boolean } {
  const [orgUsers, setOrgUsers] = useState<OrgUserOption[]>([])
  const [isLoading, setIsLoading] = useState(enabled)

  const refresh = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await getOrganizationDirectoryUsers()
      setOrgUsers(response.users.map(mapDirectoryUserToOrgUser))
    } catch {
      setOrgUsers([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!enabled) {
      setOrgUsers([])
      setIsLoading(false)
      return
    }
    void refresh()
  }, [enabled, refresh])

  return { orgUsers, isLoading }
}
